import {
  Automation,
  Brand,
  Practice,
  ValueFormatter,
} from '@principle-theorem/principle-core';
import {
  IAppointment,
  ICampaignEntryReportResult,
  ICustomReportColumn,
  ICustomReportFilter,
  IInvoice,
  IMarketingCampaign,
  IPatient,
  IPractice,
  TemplateScope,
} from '@principle-theorem/principle-core/interfaces';
import {
  IAccessorContext,
  ICanBeChartedProperty,
  IQueryScope,
  IReportingQuery,
  IScopeDataAccessor,
} from '@principle-theorem/reporting/interfaces';
import {
  asDocRef,
  asReffable,
  DocumentReference,
  WithRef,
} from '@principle-theorem/shared';
import { compact, get, sortBy, toString, uniqBy } from 'lodash';
import { ReportBuilderDataSourceInstance } from '../data-sources/report-builder-data-source-instance';
import { PatientDimensionMeasures } from '../models/dimensions/patient-dimension';
import { columnBuilder, ReportBuilderColumns } from '../report-builder-columns';
import { CampaignEntryEligibility } from './campaign-entry-eligibility';
import {
  ICampaignEntryCSVContext,
  ICampaignEntryReportContext,
} from './campaign-entry-report-context';

export class CampaignEntryReport {
  static getQueryScope(practiceRef: DocumentReference<IPractice>): IQueryScope {
    const brandRef = Practice.brandRef(asReffable(practiceRef));
    const orgRef = Brand.organisationRef(asReffable(brandRef));
    return {
      orgRef,
      scopeRequests: {
        brand: { brandRef },
        practice: { practiceRefs: [practiceRef] },
      },
    };
  }

  static getRequiredColumns(
    context: ICampaignEntryReportContext
  ): ICustomReportColumn[] | undefined {
    const patientColumns = buildPatientColumns(context.patientMeasures);
    const scopeColumns = buildScopeColumns(
      context.dataSource,
      context.scopeDataAccessor
    );
    if (!scopeColumns) {
      return;
    }
    const columns = uniqBy(
      [patientColumns, scopeColumns].flat(),
      (column) => column.id
    );
    const dataSourceHasColumns = columns.every((column) =>
      context.dataSource.findColumn(column.id)
    );
    return dataSourceHasColumns ? columns : undefined;
  }

  static getReportingQuery(
    context: ICampaignEntryReportContext,
    filters: ICustomReportFilter[]
  ): IReportingQuery | undefined {
    const requiredColumns = CampaignEntryReport.getRequiredColumns(context);
    if (!requiredColumns) {
      return;
    }

    const accessorContext: IAccessorContext = {
      timezone: context.timezone,
    };

    return context.dataSource.buildQuery(
      requiredColumns,
      [],
      filters,
      accessorContext
    );
  }

  static getReportEntry(
    context: ICampaignEntryReportContext,
    data: unknown
  ): ICampaignEntryReportResult {
    const scopeLabelMeasure =
      context.scopeDataAccessor.scopeLabelMeasure ??
      context.scopeDataAccessor.scopeMeasure;
    return {
      patientName: MeasureCast.toString(context.patientMeasures.name, data),
      patientEmail: MeasureCast.toString(context.patientMeasures.email, data),
      patientMobile: MeasureCast.toString(
        context.patientMeasures.mobileNumber,
        data
      ),
      patientRef: MeasureCast.toDocRef<IPatient>(
        context.patientMeasures.ref,
        data
      ),
      marketingOptIn: MeasureCast.toBoolean(
        context.patientMeasures.hasOptedInToMarketingCampaigns,
        data
      ),
      scopeLabel: MeasureCast.toString(scopeLabelMeasure, data),
      scopeData: Automation.buildScopeData(
        context.scope,
        MeasureCast.toDocRef(context.scopeDataAccessor.scopeMeasure, data)
      ),
    };
  }

  static getCSVEntry(
    context: ICampaignEntryCSVContext,
    data: unknown
  ): ICampaignEntryReportResult {
    const patientName = toString(get(data, 'patientName'));
    const patientEmail = toString(get(data, 'patientEmail'));
    const patientMobile = toString(get(data, 'patientMobile'));
    const patientRef = get(data, 'patientRef') as DocumentReference<IPatient>;
    // Uploads carry no opt-in column; enrolment skips patients with receiveMarketing === false.
    const marketingOptIn = true;

    if (patientRef && context.scope === TemplateScope.Patient) {
      return {
        patientName,
        patientEmail,
        patientMobile,
        patientRef,
        marketingOptIn,
        scopeLabel: patientName,
        scopeData: Automation.buildScopeData(context.scope, patientRef),
      };
    }

    const invoiceRef = get(data, 'invoiceRef') as DocumentReference<IInvoice>;
    if (invoiceRef && context.scope === TemplateScope.Invoice) {
      const invoiceReference = toString(get(data, 'invoiceReference'));
      return {
        patientName,
        patientEmail,
        patientMobile,
        patientRef,
        marketingOptIn,
        scopeLabel: invoiceReference,
        scopeData: Automation.buildScopeData(context.scope, invoiceRef),
      };
    }

    const appointmentRef = get(
      data,
      'appointmentRef'
    ) as DocumentReference<IAppointment>;
    if (appointmentRef && context.scope === TemplateScope.Appointment) {
      const appointmentLabel = toString(get(data, 'appointmentLabel'));
      return {
        patientName,
        patientEmail,
        patientMobile,
        patientRef,
        marketingOptIn,
        scopeLabel: appointmentLabel,
        scopeData: Automation.buildScopeData(context.scope, appointmentRef),
      };
    }

    throw new Error(
      `CSV entry for scope ${context.scope} is missing columns or not supported.`
    );
  }

  static getReportEntries(
    context: ICampaignEntryReportContext,
    results: unknown[]
  ): ICampaignEntryReportResult[] {
    const getScope =
      context.scopeDataAccessor.scopeMeasure.measure.dataAccessor;
    const uniqueEntries = uniqBy(results, getScope).map((result) =>
      CampaignEntryReport.getReportEntry(context, result)
    );
    return sortBy(uniqueEntries, 'name', 'scopeLabel');
  }

  static getCSVEntries(
    context: ICampaignEntryCSVContext,
    results: unknown[]
  ): ICampaignEntryReportResult[] {
    const uniqueEntries = results.map((result) =>
      CampaignEntryReport.getCSVEntry(context, result)
    );
    return sortBy(uniqueEntries, 'name', 'scopeLabel');
  }

  static async getEligibleReportEntries(
    campaign: WithRef<IMarketingCampaign>,
    context: ICampaignEntryReportContext,
    results: unknown[]
  ): Promise<ICampaignEntryReportResult[]> {
    const entries = CampaignEntryReport.getReportEntries(context, results);
    return CampaignEntryEligibility.filterEligibleEntries(campaign, entries);
  }

  static async getEligibleCSVEntries(
    campaign: WithRef<IMarketingCampaign>,
    context: ICampaignEntryCSVContext,
    results: unknown[]
  ): Promise<ICampaignEntryReportResult[]> {
    const entries = CampaignEntryReport.getCSVEntries(context, results);
    return CampaignEntryEligibility.filterEligibleEntries(campaign, entries);
  }
}

function buildPatientColumns(
  patientMeasures: PatientDimensionMeasures
): ICustomReportColumn[] {
  return ReportBuilderColumns.toColumns(
    columnBuilder((patient: PatientDimensionMeasures) => [
      patient.name,
      patient.email,
      patient.mobileNumber,
      patient.ref,
      patient.hasOptedInToMarketingCampaigns,
    ])(patientMeasures)
  );
}

function buildScopeColumns(
  datasource: ReportBuilderDataSourceInstance,
  scopeDataAccessor: IScopeDataAccessor
): ICustomReportColumn[] | undefined {
  const scopeColumn = datasource.findColumn(
    scopeDataAccessor.scopeMeasure.metadata.id
  );
  const scopeLabelColumn = scopeDataAccessor.scopeLabelMeasure
    ? datasource.findColumn(scopeDataAccessor.scopeLabelMeasure.metadata.id)
    : undefined;
  if (!scopeColumn) {
    return;
  }
  return ReportBuilderColumns.toColumns(
    compact([scopeColumn.measure, scopeLabelColumn?.measure])
  );
}

class MeasureCast {
  static toBoolean(property: ICanBeChartedProperty, data: unknown): boolean {
    return !!property.measure.dataAccessor(data);
  }

  static toString(property: ICanBeChartedProperty, data: unknown): string {
    return ValueFormatter.format(
      property.measure.labelAccessor
        ? property.measure.labelAccessor(data)
        : property.measure.dataAccessor(data),
      property.metadata.formatter,
      property.metadata.formatterValue
    );
  }

  static toDocRef<T extends object>(
    property: ICanBeChartedProperty,
    data: unknown
  ): DocumentReference<T> {
    const refPath = property.measure.dataAccessor(data).toString();
    return asDocRef<T>(refPath);
  }
}
